import _ from "lodash";
import PropTypes from "prop-types";
import React from "react";
import classes from "./CheckListContainer.scss";
import classNames from "classnames";
import Listview from "UIComponents/Listview";
import EmptyField from "UIComponents/EmptyField";
import I18nHOC from "UIComponents/I18nHOC";
import MatchingText from "UIComponents/MatchingText";
import update from "immutability-helper";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import { colors } from "Constants";
import { Checkbox, Tooltip } from "@toddle-edu/ds-web";
import {
  InformationOutlined,
  CircleOutlined,
  TickCircleFilled,
} from "@toddle-edu/ds-icons";
import { htmlToText } from "Utils/HTMLHelpers";
import ConditionalWrapper from "Utils/ConditionalWrapper";

const styles = {
  inlineTooltipStyles: {
    verticalAlign: "middle",
    marginLeft: "4px",
  },
};

class CheckList extends UIBaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      isCollapsed: true,
    };
  }

  isAllSelected = () => {
    if (this.props.options && this.props.value) {
      return this.props.options.length == this.props.value.length;
    } else {
      return false;
    }
  };

  isSelected = opt => {
    if (this.props.value) {
      return !(
        _.findIndex(this.props.value, function (o) {
          return o == opt;
        }) == -1
      );
    } else {
      return false;
    }
  };

  toggleAllCheckbox = isChecked => {
    const value = !isChecked;
    if (value) {
      //add all
      const newValue = _.map(this.props.options, option => option.value);
      this.updateValue(newValue);
    } else {
      //remove all
      this.updateValue([]);
    }
    if (this.state.error != "") {
      //this.props.errorResolve();
      this.setState({ error: "" });
    }
  };

  isDisabled = val => {
    return (
      _.findIndex(this.props.disabledList, function (o) {
        return o == val;
      }) != -1
    );
  };

  toggleCheckbox = (checkboxValue, isChecked, option) => {
    const { customToggleFn } = this.props;
    if (customToggleFn && typeof customToggleFn === "function") {
      customToggleFn(option);
      return;
    }
    const value = !isChecked;
    let newValue = this.props.value || [];
    let index = _.findIndex(this.props.value, function (o) {
      return o == checkboxValue;
    });

    if (value) {
      //add to the list
      if (index == -1) {
        index = newValue.length;
      }
      newValue = update(newValue, { $splice: [[index, 0, checkboxValue]] });
    } else {
      //remove from the list
      newValue = update(newValue, { $splice: [[index, 1]] });
    }

    this.updateValue(newValue);

    if (this.state.error != "") {
      //this.props.errorResolve();
      this.setState({ error: "" });
    }
  };

  getSelectedLabel = () => {
    const selectedLabel = [];
    const { options, value } = this.props;
    _.map(value, val => {
      selectedLabel.push(_.find(options, { value: val }).label);
    });
    return selectedLabel;
  };

  getLabelStyle = ({ isChecked }) => {
    const { listItemStyle, activeBorder } = this.props;
    const labelStyles = {
      ...listItemStyle,
    };
    if (listItemStyle.border || activeBorder) {
      labelStyles["border"] =
        isChecked && activeBorder ? activeBorder : listItemStyle.border;
    }
    return labelStyles;
  };

  getCheckListForEdit = () => {
    const {
      theme,
      isMatchingTextEnabled,
      searchText,
      tooltipPosition,
      testId,
      fillColorList,
      tickVariant,
      tickVariantList = [],
      showChecklistItemTooltip,
      checklistItemTooltipPosition,
      displayDisabledAsActive,
      informationIconSize,
      checkBoxSize,
      customLabelClass,
      checkboxStyles,
      dsVersion,
      showInlineTooltip,
    } = this.props;

    /*******************************************************************************************************
     * displayDisabledAsActive flag is used to control the visual state of this component,
     * where if it is true, we will show the element in visually active form but
     * functionality wise it will behave like disabled.
     ********************************************************************************************************/

    const searchOptions = this.props.options;
    const isDSVersionV2 = _.isEqual(dsVersion, "2.0");

    return _.map(searchOptions, (opt, index) => {
      const isChecked = this.isSelected(opt.value);
      const disabled = this.isDisabled(opt.value) || this.props.disabled;
      const labelStyles = this.getLabelStyle({ isChecked });
      const tickVariantToFill = !_.isEmpty(tickVariantList)
        ? _.get(tickVariantList, opt.value, "light")
        : tickVariant;
      const isCheckboxLabelEmpty = opt.isCheckboxLabelEmpty;
      const suffixElement = !_.isEmpty(opt.suffixElement)
        ? opt.suffixElement
        : null;

      const showOptionTooltip = _.get(opt, "showItemTooltip", false);
      const showItemTooltip = showOptionTooltip || showChecklistItemTooltip;

      const isArchived = _.get(opt, "isArchived", false);
      const labelClass = classNames({
        [classes.label]: true,
        [classes.archivedClass]: isArchived && this.props.showArchivedOptionUI,
        [classes.labelWithSuffix]: !_.isEmpty(opt.suffixElement),
        [customLabelClass]: !_.isNil(customLabelClass),
      });

      const getTooltip = ({ style = {} } = {}) => {
        return opt.subText ? (
          <Tooltip
            tooltip={<div className={classes.tooltip}>{opt.subText}</div>}
            placement={tooltipPosition ? tooltipPosition : "bottom"}
            dsVersion={dsVersion}
          >
            <InformationOutlined
              variant={"subtle"}
              size={informationIconSize}
              tabIndex={0}
              className={classes.informationIcon}
              style={style}
            />
          </Tooltip>
        ) : null;
      };

      const getLabelSpan = () => (
        <span
          style={{
            ...this.props.CheckBoxLabelStyles,
            ...(disabled ? {} : this.props.checkboxItemLabelStyle || {}),
            ...(isCheckboxLabelEmpty ? this.props.emptyLabelStyle : {}),
          }}
          dir="auto"
        >
          {isMatchingTextEnabled ? (
            <MatchingText
              searchText={searchText}
              text={htmlToText(opt.label)}
            />
          ) : (
            htmlToText(opt.label)
          )}
          {showInlineTooltip
            ? getTooltip({
                style: styles.inlineTooltipStyles,
              })
            : null}
        </span>
      );

      const checkboxOnChangeHandler = e => {
        if (!disabled) this.toggleCheckbox(opt.value, isChecked, opt);
      };

      const labelInnerWrapperClass = classNames({
        [classes.labelInnerWrapper]: true,
        ["text-body"]: !!isDSVersionV2,
      });

      return (
        <div key={index} className={labelClass} style={labelStyles}>
          <div className={labelInnerWrapperClass}>
            {theme === "CUSTOM" ? (
              <span
                data-test-id={`${testId}-checklistOption-${opt?.value}`}
                onClick={checkboxOnChangeHandler}
              >
                {this.customStyle({
                  label: htmlToText(opt.label),
                  checked: isChecked,
                })}
              </span>
            ) : (
              <ConditionalWrapper
                condition={disabled && displayDisabledAsActive}
                wrapper={<div className={classes.disabledAsActive} />}
                type={"wrapperAsElement"}
              >
                {/*******************************************************************************************************
                 * We have implemented conditionalWrapper to form a parent child relationship between the DS component
                 * and this component, to include styling for displayDisabledAsActive state, so please check the css before
                 * adding any wrapper element to the component and check that existing behaviour remains the same
                 ********************************************************************************************************/}
                <Checkbox
                  disabled={disabled && !displayDisabledAsActive}
                  isChecked={isChecked}
                  size={checkBoxSize}
                  onChange={checkboxOnChangeHandler}
                  testId={`${testId}-${_.camelCase(opt?.value)}`}
                  color={{
                    background: _.get(fillColorList, opt.value, "#008392"),
                  }}
                  tickVariant={tickVariantToFill}
                  dsVersion={dsVersion}
                  style={checkboxStyles}
                >
                  {showItemTooltip ? (
                    <Tooltip
                      placement={checklistItemTooltipPosition}
                      tooltip={htmlToText(opt.label)}
                      dsVersion={dsVersion}
                    >
                      {getLabelSpan()}
                    </Tooltip>
                  ) : (
                    getLabelSpan()
                  )}
                </Checkbox>
              </ConditionalWrapper>
            )}
            {!showInlineTooltip ? getTooltip() : null}
          </div>
          {suffixElement}
        </div>
      );
    });
  };

  customStyle = ({ label, checked }) => {
    return (
      <div className={classes.row}>
        <div className={classes.customLabel}>{label}</div>
        <div className={classes.checkIconContainer}>
          {checked ? (
            <TickCircleFilled variant="link" size="xx-small" />
          ) : (
            <CircleOutlined variant="subtle" size="xx-small" />
          )}
        </div>
      </div>
    );
  };

  shouldShowEditEmpty = () => {
    return _.get(this.props.options, "length", 0) == 0;
  };

  renderEditEmpty = () => {
    const { emptyText, emptyContainerStyle, emptyStateComponent } = this.props;

    if (emptyStateComponent) {
      return emptyStateComponent;
    }

    return (
      !!emptyText && (
        <EmptyField
          containerStyle={emptyContainerStyle}
          title={this.props.emptyText}
        />
      )
    );
  };

  renderView = () => {
    const selectedLabel = this.getSelectedLabel();
    return <Listview value={selectedLabel} mode="view" />;
  };

  renderEdit = () => {
    const { t, theme, testId, checkBoxSize, dsVersion } = this.props;
    const checkList = this.getCheckListForEdit();

    const isAllChecked = this.isAllSelected();
    const isAllDisable =
      this.props.disabled || this.props.disabledList.length > 0;

    const checkListContainerClass = classNames(
      {
        [classes.checkListContainer]: true,
      },
      {
        [classes.checkListContainerCollapsed]:
          this.props.isCollapsible && this.state.isCollapsed,
      },
      {
        [classes.checkListContainerExpanded]:
          this.props.isCollapsible && !this.state.isCollapsed,
      }
    );
    const fillColor = _.get(this.props, `fillColorList`, false)
      ? this.props.fillColorList["all"]
      : `${colors.teal200}`;

    let allOptionLabelLocale = this.props.allOptionLabel;

    if (allOptionLabelLocale === "common:all") {
      allOptionLabelLocale = this.props.t(allOptionLabelLocale);
    }

    const handleAllCheckboxChange = e => {
      if (isAllDisable) {
        return;
      }
      this.toggleAllCheckbox(isAllChecked);
    };

    const isIndeterminateValue = () => {
      if (this.props.showAllOptionIndeterminate) {
        return !this.isAllSelected() && !_.isEmpty(this.props.value);
      }
      return false;
    };

    return (
      <div className={classes.container}>
        <div
          className={checkListContainerClass}
          style={this.props.checkListContainerStyle}
        >
          {this.props.showAllOption ? (
            <div
              className={classes.label}
              style={this.props.listItemStyle}
              dir="auto"
            >
              {theme === "CUSTOM" ? (
                <span
                  data-test-id={`${testId}-toggleAll`}
                  onClick={handleAllCheckboxChange}
                >
                  {this.customStyle({
                    label: allOptionLabelLocale,
                    checked: isAllChecked,
                  })}
                </span>
              ) : (
                <Checkbox
                  testId={testId}
                  isChecked={isAllChecked}
                  disabled={isAllDisable}
                  isIndeterminate={isIndeterminateValue()}
                  size={checkBoxSize}
                  onChange={handleAllCheckboxChange}
                  dsVersion={dsVersion}
                >
                  <span style={this.props.CheckBoxLabelStyles}>
                    {allOptionLabelLocale}
                  </span>
                </Checkbox>
              )}
            </div>
          ) : null}

          {checkList}
        </div>

        {this.props.isCollapsible && this.props.options.length > 2 ? (
          <div data-test-id={`${testId}-toggleCollapsed`}>
            {this.state.isCollapsed ? (
              <div
                className={classes.expandText}
                onClick={() => this.setState({ isCollapsed: false })}
              >
                {`+ ${t("common:number_more", {
                  number: this.props.options.length - 2,
                })}`}
              </div>
            ) : (
              <div
                className={classes.expandText}
                onClick={() => this.setState({ isCollapsed: true })}
              >
                {t("common:see_less_noDots")}
              </div>
            )}
          </div>
        ) : null}
      </div>
    );
  };
}

export default I18nHOC({ resource: ["common"] })(CheckList);

CheckList.defaultProps = {
  ...UIBaseComponent.defaultProps,
  testId: `UIComponents`,
  value: [],
  disabled: false,
  listItemStyle: {},
  CheckBoxLabelStyles: {},
  checkListContainerStyle: {},
  disabledList: [],
  showAllOption: false,
  isCollapsible: false,
  offFill: `${colors.neutral500}`,
  emptyContainerStyle: {},
  allOptionLabel: "common:all",
  searchEnable: false,
  isMatchingTextEnabled: false,
  tickVariant: "light",
  showChecklistItemTooltip: false,
  checklistItemTooltipPosition: "bottom",
  showArchivedOptionUI: false,
  displayDisabledAsActive: false,
  showAllOptionIndeterminate: false,
  emptyLabelStyle: {},
  informationIconSize: "xxx-small",
  customLabelClass: null,
  dsVersion: "1.0",
  checkboxStyles: {},
  showInlineTooltip: false,
};

CheckList.propTypes = {
  ...UIBaseComponent.propTypes,
  testId: PropTypes.string,
  disabled: PropTypes.bool,
  listItemStyle: PropTypes.object,
  CheckBoxLabelStyles: PropTypes.object,
  disabledList: PropTypes.array,
  showAllOption: PropTypes.bool,
  isCollapsible: PropTypes.bool,
  checkBoxSize: PropTypes.string,
  offFill: PropTypes.string,
  emptyContainerStyle: PropTypes.object,
  allOptionLabel: PropTypes.string,
  tickVariant: PropTypes.string,
  showChecklistItemTooltip: PropTypes.bool,
  checklistItemTooltipPosition: PropTypes.string,
  displayDisabledAsActive: PropTypes.bool,
  showAllOptionIndeterminate: PropTypes.bool,
  emptyLabelStyle: PropTypes.object,
  informationIconSize: PropTypes.string,
  customLabelClass: PropTypes.string,
  dsVersion: PropTypes.string,
  checkboxStyles: PropTypes.object,
  showInlineTooltip: PropTypes.bool,
};
